import React, { useState, useEffect } from 'react'

const WithdrawModal = ({ isOpen, onClose, walletBalance, userData, onWithdrawSuccess }) => {
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [selectedBank, setSelectedBank] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [step, setStep] = useState(1)
  const [banks, setBanks] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loadingBanks, setLoadingBanks] = useState(false)
  const [bankError, setBankError] = useState(null)
  const [searchTimeout, setSearchTimeout] = useState(null)
  const [accountName, setAccountName] = useState('')
  const [verifyingAccount, setVerifyingAccount] = useState(false)
  const [verificationData, setVerificationData] = useState(null)

  // Fetch initial banks from API
  useEffect(() => {
    if (isOpen && step === 2) {
      fetchBanks()
    }
  }, [isOpen, step])

  // Handle search with debounce
  useEffect(() => {
    if (searchQuery && step === 2) {
      // Clear previous timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
      
      // Set new timeout for search
      const timeout = setTimeout(() => {
        searchBanks(searchQuery)
      }, 500) // 500ms debounce
      
      setSearchTimeout(timeout)
      
      return () => {
        clearTimeout(timeout)
      }
    } else if (!searchQuery && step === 2) {
      // If search query is empty, fetch all banks
      fetchBanks()
    }
  }, [searchQuery, step])

  // Verify account number when it changes and is 10 digits
  useEffect(() => {
    if (accountNumber.length === 10 && selectedBank && withdrawAmount) {
      verifyAccountNumber();
    } else {
      setAccountName('');
      setVerificationData(null);
    }
  }, [accountNumber, selectedBank, withdrawAmount]);

  const fetchBanks = async () => {
    setLoadingBanks(true)
    setBankError(null)
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const myHeaders = new Headers()
      myHeaders.append("Accept", "application/json")
      myHeaders.append("Authorization", `Bearer ${token}`)

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      }

      const response = await fetch("https://devapi.quickcare.ng/api/v1/onionpay/bank-list", requestOptions)
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.state === "success") {
        setBanks(result.data)
      } else {
        throw new Error(result.message || 'Failed to fetch banks')
      }
    } catch (error) {
      console.error('Error fetching banks:', error)
      setBankError(error.message)
    } finally {
      setLoadingBanks(false)
    }
  }

  const searchBanks = async (query) => {
    setLoadingBanks(true)
    setBankError(null)
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const myHeaders = new Headers()
      myHeaders.append("Accept", "application/json")
      myHeaders.append("Authorization", `Bearer ${token}`)

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        redirect: "follow"
      }

      const response = await fetch(`https://devapi.quickcare.ng/api/v1/onionpay/search-banks?query=${encodeURIComponent(query)}`, requestOptions)
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.success) {
        setBanks(result.banks)
      } else {
        throw new Error(result.message || 'Failed to search banks')
      }
    } catch (error) {
      console.error('Error searching banks:', error)
      setBankError(error.message)
    } finally {
      setLoadingBanks(false)
    }
  }

  const verifyAccountNumber = async () => {
    setVerifyingAccount(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const raw = JSON.stringify({
        account_number: accountNumber,
        bank_code: selectedBank,
        amount: parseFloat(withdrawAmount)
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      const response = await fetch("https://devapi.quickcare.ng/api/v1/onionpay/verify/account", requestOptions);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setAccountName(result.data.name);
        setVerificationData(result.data);
      } else {
        setAccountName('');
        setVerificationData(null);
        throw new Error(result.message || 'Failed to verify account');
      }
    } catch (error) {
      console.error('Error verifying account:', error);
      setAccountName('');
      setVerificationData(null);
    } finally {
      setVerifyingAccount(false);
    }
  };

  const handleAmountSubmit = (e) => {
    e.preventDefault()
    const amount = parseFloat(withdrawAmount)
    if (amount > 0 && amount <= parseFloat(walletBalance)) {
      setStep(2)
    }
  }

  const handleBankSelect = (e) => {
    e.preventDefault()
    if (selectedBank && accountNumber.length === 10 && accountName && verificationData) {
      // Save selected bank details to localStorage
      const selectedBankDetails = banks.find(bank => bank.bankCode === selectedBank)
      if (selectedBankDetails) {
        localStorage.setItem('BankDetails', JSON.stringify({
          ...selectedBankDetails,
          accountNumber,
          accountName
        }))
      }
      setStep(3)
    }
  }

  const handleWithdraw = async (e) => {
    e.preventDefault()
    setIsProcessing(true)
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const raw = JSON.stringify({
        "account_number": accountNumber,
        "bank_code": selectedBank,
        "amount": parseFloat(withdrawAmount),
        "recipient_name": accountName
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      const response = await fetch("https://devapi.quickcare.ng/api/v1/onionpay/transfer/initiate", requestOptions);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Call the success callback with the server message
        if (onWithdrawSuccess) {
          onWithdrawSuccess(result.message);
        }
        
        // Reset and close
        setWithdrawAmount('')
        setSelectedBank('')
        setAccountNumber('')
        setAccountName('')
        setVerificationData(null)
        setStep(1)
        setSearchQuery('')
        onClose()
      } else {
        throw new Error(result.message || 'Failed to process withdrawal');
      }
    } catch (error) {
      console.error('Withdrawal error:', error)
      alert('Failed to process withdrawal. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    setWithdrawAmount('')
    setSelectedBank('')
    setAccountNumber('')
    setAccountName('')
    setVerificationData(null)
    setStep(1)
    setSearchQuery('')
    setBankError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            {step === 1 && 'Withdraw Funds'}
            {step === 2 && 'Select Bank & Account'}
            {step === 3 && 'Confirm Withdrawal'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <form onSubmit={handleAmountSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount to Withdraw
                </label>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  min="1"
                  max={walletBalance}
                />
                <p className="text-sm text-gray-500 mt-2">
                  Available balance: ₦{parseFloat(walletBalance || 0).toLocaleString()}
                </p>
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
              >
                Continue
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleBankSelect}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Bank
                </label>
                
                {loadingBanks ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading banks...</p>
                  </div>
                ) : bankError ? (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    <p className="text-sm">{bankError}</p>
                    <button
                      type="button"
                      onClick={fetchBanks}
                      className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Try Again
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Search input for banks */}
                    <div className="mb-3">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search for a bank..."
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <select
                      value={selectedBank}
                      onChange={(e) => {
                        setSelectedBank(e.target.value);
                        setAccountNumber('');
                        setAccountName('');
                        setVerificationData(null);
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                      required
                      size="6" // Show more options at once
                    >
                      <option value="">Choose a bank</option>
                      {banks.map((bank) => (
                        <option key={bank.bankCode} value={bank.bankCode}>
                          {bank.name}
                        </option>
                      ))}
                    </select>
                    
                    {banks.length === 0 && searchQuery && (
                      <p className="text-sm text-gray-500 mt-2">
                        No banks found matching "{searchQuery}"
                      </p>
                    )}
                    
                    <p className="text-xs text-gray-500 mb-4">
                      {banks.length} banks found
                    </p>

                    {/* Account Number Input */}
                    {selectedBank && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Number
                        </label>
                        <input
                          type="text"
                          value={accountNumber}
                          onChange={(e) => {
                            // Only allow numbers and limit to 10 digits
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setAccountNumber(value);
                          }}
                          placeholder="Enter 10-digit account number"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                          maxLength="10"
                        />
                        {accountNumber.length > 0 && accountNumber.length !== 10 && (
                          <p className="text-sm text-red-500 mt-1">Account number must be 10 digits</p>
                        )}
                        
                        {/* Account Name Display */}
                        {verifyingAccount && (
                          <div className="mt-2 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                            <span className="text-sm text-gray-600">Verifying account...</span>
                          </div>
                        )}
                        {accountName && !verifyingAccount && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                            <p className="text-sm text-green-700 font-medium">Account Name: {accountName}</p>
                          </div>
                        )}
                        {accountNumber.length === 10 && !accountName && !verifyingAccount && (
                          <p className="text-sm text-red-500 mt-1">Could not verify account. Please check the number.</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {!loadingBanks && !bankError && (
                <button
                  type="submit"
                  disabled={!selectedBank || accountNumber.length !== 10 || !accountName || !verificationData}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors duration-200"
                >
                  Continue
                </button>
              )}
            </form>
          )}

          {step === 3 && verificationData && (
            <form onSubmit={handleWithdraw}>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Withdrawal Details</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-semibold">₦{parseFloat(withdrawAmount).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Bank:</span>
                    <span className="font-semibold">
                      {banks.find(bank => bank.bankCode === selectedBank)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Account Number:</span>
                    <span className="font-semibold">{accountNumber}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Account Name:</span>
                    <span className="font-semibold">{accountName}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Fee:</span>
                    <span className="font-semibold">₦{verificationData.fee?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Receivable:</span>
                    <span className="font-semibold">₦{verificationData.amount_receivable?.toLocaleString() || parseFloat(withdrawAmount).toLocaleString()}</span>
                  </div>
                  <div className="border-t mt-3 pt-3">
                    <div className="flex justify-between font-bold">
                      <span>Total:</span>
                      <span>₦{parseFloat(withdrawAmount).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
              >
                {isProcessing ? 'Processing...' : 'Confirm Withdrawal'}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        {step > 1 && (
          <div className="p-4 border-t bg-gray-50">
            <button
              onClick={() => {
                setStep(step - 1)
                if (step === 2) {
                  setSearchQuery('')
                }
              }}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default WithdrawModal